import { FC, useEffect, useState } from 'react'
import type { ERC20Names } from '@g4g/wallet-modal/types'
import CartButton from '../../../../shopping-cart/buttons/CartButton'
import AttentionBox from '@g4g/ui/src/components/AttentionBox'
import CheckoutPaymentSubmitReady from './CheckoutPaymentSubmitReady'
import { useCheckout } from '../../../CheckoutProvider'
import { useShoppingCartTxPayload } from './hooks/useShoppingCartTxPayload'
import { logErrorMessage } from '@g4g/utils/src/react/error'
import { ErrorCode } from '@g4g/graphql/src/shop/__generated__/globalTypes'
import { useActiveRecipientShopItems } from '@/shop/src/components/shared/hooks/useActiveRecipientShopItems'

const SubmitBlocked: FC<{ message?: string; loading: boolean; error?: Error }> = ({
  message,
  loading,
  error,
}) => {
  useEffect(() => {
    if (!error) {
      return
    }

    /**
     * fatal: true
     * Checkout errors affect conversions so mark
     * as high priority.
     */
    logErrorMessage(error, true)
  }, [error])

  return (
    <div className="flex flex-col justify-center w-full h-full space-y-2">
      <CartButton data-cy="shop-checkout-submit-btn" className="h-11" loading={loading} disabled>
        Pay Now
      </CartButton>
      {!loading && message && error && <AttentionBox>{message}</AttentionBox>}
    </div>
  )
}

const ReloadCart = () => {
  const res = useActiveRecipientShopItems()
  // `res.loading` does not get set to `true` on refetch. This is a partial
  // solution, I don't think we care so much if the refetch fails, that
  // should make the user refresh anyway. Once refetch finishes, this
  // button should go away anyhow.
  const [loading, setLoading] = useState(false)
  return (
    <div className="flex flex-col">
      <AttentionBox as="div" className="">
        <p>Some of the items are not available for the chosen recipient.</p>
        <CartButton
          loading={loading}
          color="primary"
          onClick={() => {
            res.refetch()
            setLoading(true)
          }}
          className="w-full mt-3 h-11"
          data-cy="shop-checkout-fix-cart-btn"
        >
          Fix cart
        </CartButton>
      </AttentionBox>
    </div>
  )
}

const CheckoutPaymentSubmit: FC<{
  paymentOption: ERC20Names
  loading: boolean
  account: string
  web3CheckoutError?: Error
}> = ({ paymentOption, loading: loadingTotal, account, web3CheckoutError }) => {
  const { insufficientFunds, loadingBalances } = useCheckout()
  const {
    data: payload,
    loading: loadingPayload,
    error: payloadError,
  } = useShoppingCartTxPayload({
    account,
    paymentOption,
  })

  if (web3CheckoutError) {
    return (
      <SubmitBlocked
        error={web3CheckoutError}
        loading={false}
        message="Something went wrong when trying to communicate with the connected wallet"
      />
    )
  }

  if (payloadError) {
    return (
      <SubmitBlocked
        error={payloadError}
        loading={false}
        message="Something went wrong when trying to get the transaction data"
      />
    )
  }

  if (loadingTotal || loadingBalances || loadingPayload) {
    return <SubmitBlocked loading />
  }

  if (insufficientFunds.Purpose && insufficientFunds.Dubi) {
    return <SubmitBlocked loading={false} />
  }

  if (!payload) {
    return (
      <SubmitBlocked
        loading={false}
        error={new Error(`empty payload`)}
        message={'Something went wrong (empty payload)'}
      />
    )
  }

  if (payload.buildTransaction.error === ErrorCode.RECIPIENT_INELIGIBLE) {
    return <ReloadCart />
  }

  if (!payload.buildTransaction.payload) {
    return (
      <SubmitBlocked
        loading={false}
        error={new Error(`empty buildTransaction.payload: but there was no payload error`)}
        message={'Something went wrong (no payload)'}
      />
    )
  }

  if (!payload.buildTransaction.amount) {
    return (
      <SubmitBlocked
        error={
          new Error(
            `empty burnTransaction.amount <${payload.buildTransaction.amount}>, received payload and no payload error`
          )
        }
        loading={false}
        message={`Something went wrong when calculating the total ${paymentOption} amount`}
      />
    )
  }

  return (
    <CheckoutPaymentSubmitReady
      paymentOption={paymentOption}
      amount={payload.buildTransaction.amount}
      hodlKeys={payload.buildTransaction.hodlKeys || []}
      payload={payload.buildTransaction.payload}
    />
  )
}

export default CheckoutPaymentSubmit
